<template>
    <form
        class="pb-5 mt-5 grid-cols-layout lg:grid"
        @submit.prevent="updateCustomEmails"
    >
        <div class="mb-6 lg:mb-0 lg:mt-4">
            <h3
                class="mb-[5px] text-lg font-medium leading-normal tracking-small text-gray-800"
            >
                Subscription invoice emails
            </h3>
            <p class="max-w-xs text-sm text-gray-500">
                You can receive subscription invoices by email. Choose the email
                addresses where you'd like to receive them. You can add up to 5
                emails.
            </p>
        </div>
        <div class="w-full mx-auto bg-white rounded-md shadow-card lg:mx-0">
            <div class="flex items-center p-6">
                <div class="flex flex-col flex-wrap gap-6 sm:flex-row">
                    <div
                        class="w-full rounded-md border border-gray-300 bg-white text-sm sm:w-[300px]"
                    >
                        <div
                            v-for="email in emails"
                            :key="email"
                            class="border-b border-gray-200 cursor-pointer"
                        >
                            <div
                                class="flex items-center justify-between p-2 text-gray-600 cursor-pointer group hover:bg-gray-100"
                            >
                                <div class="flex items-center gap-2">
                                    <span class="py-1">{{ email }}</span>
                                    <PencilSquareIcon
                                        class="hidden w-6 h-6 text-gray-500 cursor-pointer shrink-0 hover:text-gray-700 group-hover:block"
                                        @click="editItem(email)"
                                    />
                                </div>
                                <XMarkIcon
                                    class="hidden w-6 h-6 text-gray-500 cursor-pointer shrink-0 hover:text-gray-700 group-hover:block"
                                    @click="deleteItem(email)"
                                />
                            </div>
                        </div>
                        <div
                            class="flex items-center justify-center h-10 cursor-pointer group rounded-b-md hover:bg-gray-100"
                            @click="createItem"
                        >
                            <PlusCircleIcon
                                class="w-6 h-6 text-gray-500 group-hover:text-gray-700"
                            />
                        </div>
                    </div>
                </div>
            </div>

            <div class="flex justify-end gap-4 px-6 py-3">
                <LockableButton
                    type="submit"
                    color="yellow"
                    size="sm"
                    :disabled="saveDisabled"
                    :locked="!isAdmin && !isOwner"
                    lock-message="Only the team owner and admin can update the subscription invoice emails."
                >
                    Save changes
                </LockableButton>
            </div>
        </div>

        <SubscriptionInvoiceSettingsModal
            v-if="showModal"
            :email="selectedEmail"
            @close="handleCloseModal"
            @submit="updateEmails"
        />
    </form>
</template>
<script lang="ts" setup>
import { PropType } from 'vue';
import {
    XMarkIcon,
    PencilSquareIcon,
    PlusCircleIcon,
} from '@heroicons/vue/20/solid';
import useConfirm from '@composables/useConfirm';

const props = defineProps({
    modelValue: {
        type: Array as PropType<string[]>,
        required: true,
    },
});

const emit = defineEmits(['update:modelValue']);

const { show: showConfirmation } = useConfirm();
const showModal = ref(false);
const selectedEmail = ref('');
const maxCount = 5;

const { isAdmin, isOwner } = usePermissions();

const handleCloseModal = () => {
    showModal.value = false;
    selectedEmail.value = '';
};

const saveDisabled = computed(() => {
    if (props.modelValue.length !== emails.value.length) {
        return false;
    }

    return props.modelValue.every(
        (email, index) => email === emails.value[index]
    );
});

const emails = ref([...props.modelValue]);

const updateEmails = (email: string) => {
    const foundDuplicate = emails.value.find((e) => e === email);
    if (foundDuplicate) {
        handleCloseModal();
        return;
    }

    const index = emails.value.findIndex((e) => e === selectedEmail.value);

    if (index === -1) {
        if (emails.value.length >= maxCount) {
            handleCloseModal();

            create({
                title: 'Max count reached. You can add up to 5 emails.',
                type: 'info',
            });

            return;
        }
        emails.value.push(email);
    } else {
        emails.value[index] = email;
    }

    handleCloseModal();
};

const updateCustomEmails = () => {
    emit('update:modelValue', emails.value);
};

const createItem = () => {
    selectedEmail.value = '';
    showModal.value = true;
};

const editItem = (email: string) => {
    selectedEmail.value = email;
    showModal.value = true;
};

const deleteItem = async (email: string) => {
    const confirmed = await showConfirmation({
        header: 'Delete email',
        message: `Do you really want to delete this ${email}?`,
        type: 'danger',
    });

    if (!confirmed) {
        return;
    }

    emails.value = emails.value.filter((e) => e !== email);
};
</script>
